import React, { type PropsWithChildren, useMemo } from 'react';
import styled from 'styled-components';
import { ButtonCondensed } from '@companycam/slab-web';
import Bugsnag from '@bugsnag/js';
import { translate } from 'javascripts/translations/i18n';

const ErrorBoundary = Bugsnag.getPlugin('react').createErrorBoundary(React);

type CommentErrorBoundaryProps = PropsWithChildren<{
  onReset: () => void;
}>;

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: var(--cc_size_spacing_m);
  background-color: var(--cc_color_background_1);
  border: 1px solid var(--cc_color_border_default);
  border-radius: var(--cc_size_border_radius_l);
  margin-bottom: var(--cc_size_spacing_s);
`;

const ErrorMessage = styled.p`
  color: var(--cc_color_text_error);
  font-size: var(--cc_size_text_s);
  margin-bottom: var(--cc_size_spacing_s);
`;

export const CommentErrorBoundary = ({
  children,
  onReset,
}: CommentErrorBoundaryProps) => {
  const Fallback = useMemo(() => {
    return function (props: { clearError: () => void }) {
      return (
        <ErrorContainer data-testid="comment__input-error-boundary">
          <ErrorMessage>
            {translate('activity.tokenizedInput.somethingWentWrong')}
          </ErrorMessage>
          <ButtonCondensed
            data-testid="comment__error-input-boundary-reset"
            color="primary"
            onClick={() => {
              props.clearError();
              onReset();
            }}
          >
            {translate('common.retry')}
          </ButtonCondensed>
        </ErrorContainer>
      );
    };
  }, [onReset]);

  return <ErrorBoundary FallbackComponent={Fallback}>{children}</ErrorBoundary>;
};
